import { useState, useEffect } from "react";
import { Loader2, Plus, Megaphone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState, StatCard } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { formatGs, formatDate } from "@/lib/format";

const PLATFORMS = ["Meta Ads", "Instagram Ads", "TikTok Ads", "Google Ads", "Otras"];
const today = new Date().toISOString().slice(0, 10);
const EMPTY = { date: today, platform: "Meta Ads", campaign: "", amount: 0, note: "" };

const Traffic = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => { setLoading(true); api.get("/admin/traffic").then((r) => setItems(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.amount) return toast.error("Ingresá el monto");
    try { await api.post("/admin/traffic", form); toast.success("Registrado"); setOpen(false); setForm(EMPTY); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { await api.delete(`/admin/traffic/${id}`); load(); };

  const total = items.reduce((a, b) => a + b.amount, 0);

  return (
    <div>
      <PageHeader title="Tráfico pago" subtitle="Registrá el gasto en publicidad"
        action={<Button onClick={() => { setForm(EMPTY); setOpen(true); }} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="new-traffic-btn"><Plus className="h-4 w-4 mr-1" /> Registrar inversión</Button>} />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        <StatCard label="Inversión total" value={formatGs(total)} icon={Megaphone} accent="amber" />
        <StatCard label="Registros" value={items.length} icon={Megaphone} accent="indigo" />
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>
        : items.length === 0 ? <EmptyState icon={Megaphone} text="Sin registros de tráfico." />
        : (
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {items.map((t) => (
              <div key={t.id} className="flex items-center justify-between p-4" data-testid={`traffic-${t.id}`}>
                <div>
                  <p className="font-medium text-slate-900">{t.platform} <span className="text-slate-400 font-normal">· {t.campaign || "Sin campaña"}</span></p>
                  <p className="text-xs text-slate-400">{formatDate(t.date)} {t.note && `· ${t.note}`}</p>
                </div>
                <div className="flex items-center gap-3">
                  <p className="font-semibold text-slate-900">{formatGs(t.amount)}</p>
                  <button onClick={() => del(t.id)} className="text-rose-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Registrar inversión</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Fecha</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-lg mt-1" /></div>
            <div><Label>Plataforma</Label>
              <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                <SelectTrigger className="rounded-lg mt-1" data-testid="traffic-platform"><SelectValue /></SelectTrigger>
                <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Campaña</Label><Input value={form.campaign} onChange={(e) => setForm({ ...form, campaign: e.target.value })} className="rounded-lg mt-1" /></div>
            <div><Label>Inversión (₲)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} className="rounded-lg mt-1" data-testid="traffic-amount" /></div>
            <div><Label>Observación</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="rounded-lg mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-lg">Cancelar</Button>
            <Button onClick={save} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="save-traffic">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Traffic;
